import { IOption } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.types';
import { RowData } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { TOnActionType } from '~/services/analytics/analytics.types';
import { IOwner, ITeamMember, IVendorEmail } from '~/types/model.types';
import { IMember, IWorkspaceMembersLiteResponse } from '~/types/server.types';
import { TTasksAdvancedFilters } from '~/apps/main/Workspace/TasksTab/workspace-tasks.types';
import { ValueOf } from 'type-fest';

export type TTaskServerStatuses =
    | 'not_required'
    | 'required'
    | 'approved'
    | 'pending'
    | 'completed'
    | 'declined';

export type TTaskSource =
    | 'manual'
    | 'workflow'
    | 'automation'
    | 'ai_action_item'
    | 'onboarding';

export type TTaskFilter =
    | 'overdue'
    | 'all'
    | 'today'
    | 'completed'
    | 'this_week'
    | 'approvals';

export enum TaskFilters {
    OVERDUE = 'overdue',
    ALL = 'all',
    TODAY = 'today',
    COMPLETED = 'completed',
    THIS_WEEK = 'this_week',
    APPROVALS = 'approvals'
}

export enum TaskStatus {
    NOT_REQUIRED = 'not_required',
    OVERDUE = 'overdue',
    OVERDUE_APPROVAL_PENDING = 'overdue_approval_pending',
    APPROVAL_PENDING = 'approval_pending',
    REQUIRED = 'required',
    APPROVED = 'approved',
    PENDING = 'pending',
    COMPLETED = 'completed',
    DECLINED = 'declined'
}
export interface ITaskEmailResponse
    extends Omit<IVendorEmail, 'dataOnly' | 'time_to_send'> {}

type TReminderUnitType = 'minutes' | 'hours' | 'days';

export type TTaskReminder = {
    unit_count: number;
    unit_type: TReminderUnitType;
    reminder_timer_id?: string;
};

export const reminderLabels = [
    '0_minutes',
    '5_minutes',
    '10_minutes',
    '15_minutes',
    '1_hours',
    '2_hours',
    '1_days',
    '2_days'
] as const;

export type TReminderLabels = (typeof reminderLabels)[number];

export interface ITaskResponse {
    _id: string;
    creator: IMember | IOwner | ITeamMember;
    assignee: IMember | IOwner | ITeamMember;
    description: string;
    status: TTaskServerStatuses;
    source?: TTaskSource;
    attachment_count?: number;
    due_date?: string;
    due_time?: string;
    email_template_id?: string;
    has_depended_step?: boolean;
    project_id?: string;
    project_name?: string;
    task_group_id?: string;
    type?: string; // ##### TODO: TBD
    workflow_id?: string;
    workflow_name?: string;
    workspace_id?: string;
    is_overdue: boolean;
    reminder?: TTaskReminder | null;
    workspace_team_members?: IWorkspaceMembersLiteResponse[];
}

export interface ITaskReminderNotificationData {
    description: string;
    due_date: string;
    due_time: string;
    task_id: string;
    workspace_id?: string;
    event_id?: string;
}

export interface IFetchTasksResponse {
    tasks: ITaskResponse[];
    offsets: {};
}

export interface ITaskCountResponse {
    all: number;
    approvals: number;
    completed: number;
    overdue: number;
    tasks: number;
    this_week: number;
    today: number;
    total: number; // includes completed tasks
}

export interface IWorkspacesTaskCountResponse {
    workspace_id: string;
    tasks_count: number;
}

export interface ITaskSort {
    sortBy: TTaskSortBy;
    sortDesc: boolean;
}

export interface ICreateTaskRequest {
    description?: string;
    workspace_id?: string;
    due_date?: string;
    due_time?: string;
    origin?: TTaskOrigin;
    assignee_id?: string;
    status?: TTaskServerStatuses;
    source?: TTaskSource;
    reminder?: TTaskReminder;
    task_group_id?: string;
}

export interface TaskSchema
    extends Omit<ICreateTaskRequest, 'due_date' | 'status' | 'reminder'> {
    due_date: Date;
    isCompleted: boolean;
    reminder?: TReminderLabels;
}

export interface IFetchTasksRequest extends TTasksAdvancedFilters {
    filter_by?: TTaskFilterBy;
    page?: number; // ### TODO: Check type
    perPage?: number;
    sort_by?: TTaskSortBy;
    sort_desc?: boolean;
    tasks_misc_offset?: number;
    wf_approvals_misc_offset?: number;
    wf_approvals_after_step_execution_offset?: number;
    is_home_card_data?: boolean;
}

export interface IFetchUserTaskCountRequest {
    curr_date: string;
}

export interface ITasksParams {
    workspaceId?: string;
    origin?: TTaskOrigin;
    analyticsOrigin?: string;
}

export type TTaskOrigin =
    | 'task_management'
    | 'workspace'
    | 'onboarding'
    | 'ios_task_management'
    | 'android_task_management'
    | 'home_card'
    | 'priority_list';

export type TTaskFilterBy =
    | 'all'
    | 'today'
    | 'this_week'
    | 'overdue'
    | 'approvals'
    | 'tasks'
    | 'completed';

export type TTaskSortBy =
    | 'description'
    | 'workflow_name'
    | 'project_name'
    | 'due_date';

export enum TaskSortBy {
    DESCRIPTION = 'description',
    WORKFLOW_NAME = 'workflow_name',
    PROJECT_NAME = 'project_name',
    DUE_DATE = 'due_date'
}

type TTableColumnItemType =
    | 'input'
    | 'select-date'
    | 'search'
    | 'text'
    | 'checkbox'
    | 'custom';

export type TOnActionNameType = 'onChange' | 'onClick' | 'onBlur';

export type HBTableColumnItems = {
    id: string;
    header: string;
    type: TTableColumnItemType;
}[]; // The ID should be the key of the data you want in the column.

export type TTableCellAlignType = 'left' | 'center' | 'right';
export type TTableCellMobileAlignType = 'start' | 'center' | 'end';
export interface HBTableColumnOption {
    id: string;
    dbFieldName?: string;
    accessorFn?: (dataObject: ITaskResponse) => ValueOf<ITaskResponse>;
    header: string;
    type: TTableColumnItemType;
    width?: string;
    overflow?: 'auto' | 'hidden' | 'scroll' | 'visible';
    flexGrow?: number;
    align?: TTableCellAlignType;
    columnInfoIconText?: ReactNode;
    columnInfoTooltipMaxWidth?: string;
    isSortable?: boolean;
    isPlaceholder?: boolean;
    placeholder?: string;
    gridArea?: string;
    mobileAlign?: TTableCellMobileAlignType;
    isEditable?: (rowData: ITaskResponse) => boolean;
    searchFn?: (query: string) => Promise<{ label: string; value: string }[]>;
    searchDropdownDefaultValues?: IOption[];
    customComponent?: {
        component: React.ComponentType<any>;
        props?: any;
        onAction?: (value: ITaskResponse) => void;
        onActionName?: TOnActionNameType;
    };
    checkIfShouldHideComponent?: (rowData: ITaskResponse) => boolean;
    shouldHideCell?: boolean;
}

export interface ITableCellProps {
    value: any;
    setValue: (value: any) => void;
    updateData: (data: any) => void;
    dbFieldName: string;
    placeholder: string;
    isCompleted: boolean;
    isEditable: boolean;
}

export interface IRowCustomStyle {
    padding?: string;
    mWebPadding?: string;
    margin?: string;
    mWebMargin?: string;
}
export interface ICellCustomStyle {
    padding?: string;
    mWebPadding?: string;
}

declare module '@tanstack/react-table' {
    export interface TableMeta<TData extends RowData> {
        updateData?: ({
            objectToUpdate,
            updatedValues
        }: {
            objectToUpdate: TData;
            updatedValues: Partial<TData>;
        }) => void;
        gridTemplateAreas?: string;
        gridTemplateColumns?: string;
        rowCustomStyle?: IRowCustomStyle;
        cellCustomStyle?: ICellCustomStyle;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    export interface ColumnMeta<TData extends RowData, TValue> {
        type?: TTableColumnItemType;
        width?: string;
        overflow?: 'auto' | 'hidden' | 'scroll' | 'visible';
        flexGrow?: number;
        align?: TTableCellAlignType;
        placeholder?: string;
        columnInfoIconText?: string;
        columnInfoTooltipMaxWidth?: string;
        searchFn?: (
            query: string
        ) => Promise<{ label: string; value: string }[]>;
        searchDropdownDefaultValues?: IOption[];
        dbFieldName?: string;
        isSortable?: boolean;
        customComponent?: {
            component: React.ComponentType;
            props?: {};
            onAction?: (value: ITaskResponse) => void;
            onActionName?: TOnActionType;
        };
        gridArea?: string;
        mobileAlign?: TTableCellMobileAlignType;
        shouldHideCell?: boolean;
    }
}
